import { createApp, type App } from 'vue';
import { createPinia } from 'pinia';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { createApiClient, ApiClientKey } from '@/shared/api/client';
import { useAuthStore } from '@/shared/stores/auth';
import { createAuthApi } from '@/modules/auth/api';
import { applyBranding } from '@/shared/composables/useBranding';
import router, { markSessionReady } from '@/router';
import AppRoot from './App.vue';

export function bootstrap(): App {
  applyBranding();

  const app = createApp(AppRoot);
  const pinia = createPinia();
  app.use(pinia);
  app.use(VueQueryPlugin);

  const auth = useAuthStore();

  const apiClient = createApiClient({
    baseURL: import.meta.env.VITE_API_BASE_URL,
    getAccessToken: () => auth.accessToken,
    getRefreshToken: () => auth.refreshToken,
    onTokensRefreshed: (tokens) => {
      auth.setTokens({
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token,
        expires_at: tokens.expires_at,
        refresh_expires_at: tokens.refresh_expires_at,
      });
    },
    onSessionExpired: () => {
      auth.clear();
      router.replace({ name: 'auth.login' });
    },
  });

  app.provide(ApiClientKey, apiClient);
  app.use(router);

  void (async () => {
    if (auth.accessToken && !auth.user) {
      try {
        const me = await createAuthApi(apiClient).me();
        auth.setUser(me);
      } catch {
        auth.clear();
        await router.replace({ name: 'auth.login' });
      }
    }
    markSessionReady();
  })();

  return app;
}
